import { useMemo } from 'react';
import { scaleLinear, quantile, median } from 'd3';
import { Chart } from './Chart';

function p95(arr) {
  if (arr.length === 0) return null;
  const s = [...arr].sort((a, b) => a - b);
  return quantile(s, 0.95);
}

// Returns scatter points using whichever pair of telemetry signals has data.
// X always = top speed. Y = lateral g if available, else braking g. The third
// signal (whichever is left) sizes the dot. lateral_g often comes back null
// from the FastF1 worker — its calc requires position data and a sanity check
// that frequently fails for older sessions — so we degrade gracefully.
function buildPoints(laps) {
  const byDriver = new Map();
  for (const l of laps) {
    if (l.is_pit_in || l.is_pit_out) continue;
    if (!byDriver.has(l.driver)) byDriver.set(l.driver, { ts: [], lat: [], dec: [] });
    const acc = byDriver.get(l.driver);
    if (l.top_speed_kph != null) acc.ts.push(Number(l.top_speed_kph));
    if (l.max_lateral_g != null) acc.lat.push(Number(l.max_lateral_g));
    if (l.max_decel_g != null) acc.dec.push(Number(l.max_decel_g));
  }

  let driversWithLat = 0;
  let driversWithDec = 0;
  let driversWithTop = 0;
  for (const acc of byDriver.values()) {
    if (acc.ts.length) driversWithTop++;
    if (acc.lat.length) driversWithLat++;
    if (acc.dec.length) driversWithDec++;
  }

  if (driversWithTop < 2) return { points: [], yField: null, sizeField: null };

  const useLat = driversWithLat >= 2;
  const yField = useLat ? 'lat' : driversWithDec >= 2 ? 'dec' : null;
  const sizeField = useLat && driversWithDec >= 2 ? 'dec' : null;

  if (!yField) return { points: [], yField: null, sizeField: null };

  const out = [];
  for (const [driver, acc] of byDriver.entries()) {
    const top = p95(acc.ts);
    const lat = p95(acc.lat);
    const dec = p95(acc.dec);
    const yVal = yField === 'lat' ? lat : dec;
    if (top == null || yVal == null) continue;
    out.push({
      driver,
      top,
      y: yVal,
      size: sizeField === 'dec' ? dec : null,
      lat,
      dec,
    });
  }
  return { points: out, yField, sizeField };
}

const Y_LABELS = {
  lat: { axis: 'lateral g', unit: 'g', tooltip: 'lateral g (p95)' },
  dec: { axis: 'braking g', unit: 'g', tooltip: 'braking g (p95)' },
};

export const CarCharacter = ({ laps }) => {
  const { points, yField, sizeField } = useMemo(() => buildPoints(laps || []), [laps]);

  if (points.length < 2) {
    return (
      <div className="absolute inset-0 grid place-items-center text-xs text-f1-muted px-6 text-center">
        Telemetry footprint not available for this session.
      </div>
    );
  }

  const yMeta = Y_LABELS[yField];

  return (
    <Chart>
      {({ width, height }) => {
        const margin = { top: 14, right: 14, bottom: 22, left: 30 };
        const innerW = Math.max(0, width - margin.left - margin.right);
        const innerH = Math.max(0, height - margin.top - margin.bottom);

        const xs = points.map((p) => p.top);
        const ys = points.map((p) => p.y);
        const ds = sizeField ? points.map((p) => p.size).filter((v) => v != null) : [];
        const xMin = Math.min(...xs), xMax = Math.max(...xs);
        const yMin = Math.min(...ys), yMax = Math.max(...ys);
        const xPad = (xMax - xMin) * 0.08 || 1;
        const yPad = (yMax - yMin) * 0.08 || 0.05;

        const x = scaleLinear().domain([xMin - xPad, xMax + xPad]).range([0, innerW]);
        const y = scaleLinear().domain([yMin - yPad, yMax + yPad]).range([innerH, 0]);

        const xMed = median(xs);
        const yMed = median(ys);

        const dRange = ds.length ? [Math.min(...ds), Math.max(...ds)] : [0, 1];
        const r = scaleLinear().domain(dRange).range([3, 7]).clamp(true);

        return (
          <svg width={width} height={height}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              {/* crosshair = field median */}
              <line x1={x(xMed)} x2={x(xMed)} y1={0} y2={innerH} stroke="#3f3f46" strokeDasharray="2,3" />
              <line x1={0} x2={innerW} y1={y(yMed)} y2={y(yMed)} stroke="#3f3f46" strokeDasharray="2,3" />

              {/* quadrant labels */}
              <text x={innerW - 2} y={10} textAnchor="end" fontSize="8" fill="#52525b">
                straights + corners
              </text>
              <text x={2} y={10} fontSize="8" fill="#52525b">grippy, slow</text>
              <text x={2} y={innerH - 2} fontSize="8" fill="#52525b">draggy, slow</text>
              <text x={innerW - 2} y={innerH - 2} textAnchor="end" fontSize="8" fill="#52525b">
                low-drag straights
              </text>

              {points.map((p) => (
                <g key={p.driver}>
                  <circle
                    cx={x(p.top)}
                    cy={y(p.y)}
                    r={p.size != null ? r(p.size) : 4}
                    fill="#e10600"
                    opacity={0.55}
                    stroke="#e5e5e5"
                    strokeWidth={0.6}
                  >
                    <title>
                      {p.driver}
                      {`\ntop speed (p95): ${p.top.toFixed(0)} kph`}
                      {p.lat != null ? `\nlateral g (p95): ${p.lat.toFixed(2)}g` : ''}
                      {p.dec != null ? `\nbraking g (p95): ${p.dec.toFixed(2)}g` : ''}
                    </title>
                  </circle>
                  <text
                    x={x(p.top) + 6}
                    y={y(p.y) + 3}
                    fontSize="9"
                    fill="#e5e5e5"
                  >
                    {p.driver}
                  </text>
                </g>
              ))}

              {/* axis ticks */}
              <text x={0} y={innerH + 12} fontSize="9" fill="#9ca3af">
                {Math.round(xMin)}
              </text>
              <text x={innerW} y={innerH + 12} textAnchor="end" fontSize="9" fill="#9ca3af">
                {Math.round(xMax)} kph
              </text>
              <text x={-4} y={innerH} textAnchor="end" fontSize="9" fill="#9ca3af">
                {yMin.toFixed(1)}
              </text>
              <text x={-4} y={10} textAnchor="end" fontSize="9" fill="#9ca3af">
                {yMax.toFixed(1)}{yMeta.unit}
              </text>
              {/* y-axis label so the user knows which signal we fell back to */}
              <text
                x={-margin.left + 2}
                y={-2}
                fontSize="8"
                fill="#6b7280"
              >
                y: {yMeta.axis}
              </text>
            </g>
          </svg>
        );
      }}
    </Chart>
  );
};
